<template>
  <div class="mt-10" data-testid="page-my-leads">
    <h2 class="txt-display-s-semibold">My Leads</h2>
    <ContentCard class="mt-7">
      <div class="flex">
        <SecondarySidebar v-if="hasVisibleSidebarItems" :sidebar-items="visibleSidebarItems" />
        <RouterView
          v-if="hasVisibleSidebarItems"
          :slug="route.params.slug"
          :loading="loading"
          :has-root-event="hasRootEvent"
          :event-types="currentEventTypes"
        />
        <MyleadsPlaceholder v-else :loading="loading" />
      </div>
    </ContentCard>
  </div>
</template>

<script setup lang="ts">
import type { ApolloQueryResult } from '@apollo/client/core'
import { computed, reactive, nextTick, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import { SecondarySidebar } from '@/components/SecondarySidebar'
import {
  useLeadCountsByEventQuery,
  EventTypeEnum,
  type LeadCountsByEventQuery,
  usePartnerCompanyLeadCountQuery,
  type PartnerCompanyLeadCountQuery,
} from '@/gql/myomr'
import { useRoleRedirect } from '@/services/roleRedirect'
import { useMeStore } from '@/stores'

useRoleRedirect('viewLeads')

const meStore = useMeStore()

const route = useRoute()
const router = useRouter()
const omrEventTypes = [EventTypeEnum.Masterclass, EventTypeEnum.SideEvent, EventTypeEnum.GuidedTour]
const dmcEventTypes = [EventTypeEnum.DigitalMasterclass]

// omr99 is the sales demo season on staging, seeded by myomr's one_off:seed_demo_sales.
const availableSidebarItems = {
  omr99: { label: 'OMR99', route: { name: 'eventLeads', params: { slug: 'omr99' } } },
  omr27: { label: 'OMR27', route: { name: 'eventLeads', params: { slug: 'omr27' } } },
  omr26: { label: 'OMR26', route: { name: 'eventLeads', params: { slug: 'omr26' } } },
  omr25: { label: 'OMR25', route: { name: 'eventLeads', params: { slug: 'omr25' } } },
  omr24: { label: 'OMR24', route: { name: 'eventLeads', params: { slug: 'omr24' } } },
  dmc: { label: 'Digital Masterclasses', route: { name: 'eventLeads', params: { slug: 'dmc' } } },
  reviews: { label: 'OMR Reviews', route: { name: 'eventLeads', params: { slug: 'reviews' } } },
} as const
type AvailableSidebarItemKey = keyof typeof availableSidebarItems
const sidebarItemVisibility = reactive<{ [key in AvailableSidebarItemKey]: boolean }>({
  omr99: false,
  omr27: false,
  omr26: false,
  omr25: false,
  omr24: false,
  dmc: false,
  reviews: false,
})
const eventKeys = Object.keys(availableSidebarItems).filter((key) => /omr\d{2}/.test(key))

const visibleSidebarItemKeys = computed(() => {
  return Object.keys(availableSidebarItems).filter((key) => sidebarItemVisibility[key as AvailableSidebarItemKey])
})
const visibleSidebarItems = computed(() => {
  return visibleSidebarItemKeys.value.map((key) => availableSidebarItems[key as AvailableSidebarItemKey])
})

// OMR main event related queries
const eventQueries = eventKeys.map((key) => {
  const eventKey = key as AvailableSidebarItemKey
  const eventQuery = useLeadCountsByEventQuery(() => ({ rootEvents: [{ slug: eventKey }], type: omrEventTypes }))
  eventQuery.onResult((queryResult) => afterEventsQueryHook(eventKey, queryResult))
  return eventQuery
})
const eventQueriesLoading = computed(() => eventQueries.some((query) => query.loading.value))

function afterEventsQueryHook(sidebarItemKey: AvailableSidebarItemKey, queryResult: ApolloQueryResult<LeadCountsByEventQuery>) {
  if (
    !queryResult.loading &&
    queryResult.data?.events?.nodes?.length &&
    queryResult.data?.events?.nodes?.reduce((acc, event) => acc + (event?.leadCount || 0), 0)
  ) {
    sidebarItemVisibility[sidebarItemKey] = true
  }
}

// Partner company related queries, by main event
const partnerCompanyQueries = eventKeys.map((key) => {
  const eventKey = key as AvailableSidebarItemKey
  const partnerCompanyQuery = usePartnerCompanyLeadCountQuery(() => ({
    mainEventId: key,
    partnerCompanyId: meStore.currentPartnerCompanyGlobalId as string,
  }))
  partnerCompanyQuery.onResult((queryResult) => afterPartnerCompanyQueryHook(eventKey, queryResult))
  return partnerCompanyQuery
})
const partnerCompaniesLoading = computed(() => partnerCompanyQueries.some((query) => query.loading.value))

function afterPartnerCompanyQueryHook(
  sidebarItemKey: AvailableSidebarItemKey,
  queryResult: ApolloQueryResult<PartnerCompanyLeadCountQuery>,
) {
  if (!queryResult.loading && queryResult.data?.leads?.totalCount) {
    sidebarItemVisibility[sidebarItemKey] = true
  }
}

// DMC related queries
const { onResult: onDmcResult, loading: dmcLoading } = useLeadCountsByEventQuery(() => ({ type: [EventTypeEnum.DigitalMasterclass] }))
onDmcResult((queryResult) => afterEventsQueryHook('dmc', queryResult))

const loading = computed(() => eventQueriesLoading.value || dmcLoading.value || partnerCompaniesLoading.value)

const hasVisibleSidebarItems = computed(() => visibleSidebarItemKeys.value.length > 0)

watch([hasVisibleSidebarItems], async () => {
  await nextTick()
  if (route.params.slug !== undefined && visibleSidebarItemKeys.value.includes(route.params.slug as string)) return
  redirectToFirstVisibleSidebarItem()
})

async function redirectToFirstVisibleSidebarItem() {
  if (!visibleSidebarItemKeys.value[0]) return
  router.push({ name: 'eventLeads', params: { slug: visibleSidebarItemKeys.value[0] } })
}

const eventConfig = {
  omr99: { hasRootEvent: true, eventTypes: omrEventTypes },
  omr27: { hasRootEvent: true, eventTypes: omrEventTypes },
  omr26: { hasRootEvent: true, eventTypes: omrEventTypes },
  omr25: { hasRootEvent: true, eventTypes: omrEventTypes },
  omr24: { hasRootEvent: true, eventTypes: omrEventTypes },
  dmc: { hasRootEvent: false, eventTypes: dmcEventTypes },
  reviews: { hasRootEvent: false, eventTypes: [] },
}

const hasRootEvent = computed(() => eventConfig[route.params?.slug as AvailableSidebarItemKey]?.hasRootEvent || false)
const currentEventTypes = computed(() => eventConfig[route.params?.slug as AvailableSidebarItemKey]?.eventTypes || [])
</script>
